/*

? The voice-note attachments of one chat message (recorded in the app, 15 Oct 2026): the
? browser's own audio player, one per note, after the sender's picture with a small mic badge
? (19 Oct 2026, the chat cards redesign, the app's look; no waveform).

The server resolves each attachment to a signed `file_url` (lib/chat/resolveChatAttachment.ts).
A note that didn't resolve (`exists: false`) is left out rather than shown as a dead player.
The player streams the signed link as it plays (`preload="metadata"` reads only the length up
front). Photos keep going to ImageStack and documents to ChatFileTiles.

*/

/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Component Imports */

import { ChatAvatar } from "./ChatMessageCards";

/* Icon Imports */

import { Mic } from "@mui/icons-material";

/* Styled Components */

const NoteList = styled("div")({
	display: "flex",
	flexDirection: "column",
	gap: "0.375rem",
	width: "18rem",
	maxWidth: "100%",
});

const NoteRow = styled("div")({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	// The mic badge hangs past the avatar, so the player keeps a little extra room (19 Oct 2026).
	gap: "1rem",
	width: "100%",
});

const Player = styled("audio")({
	flex: 1,
	minWidth: 0,
	height: "40px",
});

/* The mic badge on the avatar's bottom-right corner. */
const MicBadge = styled("span")(({ theme }) => ({
	position: "absolute",
	right: "-0.25rem",
	bottom: "-0.25rem",
	width: "1.125rem",
	height: "1.125rem",
	borderRadius: "50%",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	background: theme.palette.primary.dark,
	color: theme.palette.primary.contrastText,
	border: `2px solid ${theme.palette.background.paper}`,
}));

const ChatVoiceNotes = ({
	notes,
	sender_image_url,
	sender_name,
}: {
	notes: Array<any>;
	/* Who recorded it: the teammate on the viewer's own side, else the other side's picture. */
	sender_image_url?: string;
	sender_name?: string;
}) => {
	const shown = notes.filter((note: any) => note && note.exists !== false && note.file_url);
	if (shown.length === 0) return null;
	return (
		<NoteList>
			{shown.map((note: any, index: number) => (
				// Keyed by the file, so a playing note never moves to another bubble when the thread shifts (chat audit I12).
				<NoteRow key={String(note.file_id || "") || index}>
					<ChatAvatar
						src={sender_image_url}
						name={sender_name || ""}
						size="2.75rem"
						badge={
							<MicBadge>
								<Mic sx={{ fontSize: "0.6875rem" }} />
							</MicBadge>
						}
					/>
					<Player
						controls
						preload="metadata"
						src={note.file_url}
					/>
				</NoteRow>
			))}
		</NoteList>
	);
};

export default ChatVoiceNotes;
